import { X, Heart, ShoppingBag, Share2 } from 'lucide-react';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const WishlistDrawer = () => {
  const { items, isOpen, closeWishlist, removeItem } = useWishlist();
  const { addItem } = useCart();

  const handleAddToCart = (item: typeof items[0]) => {
    addItem({
      id: item.id,
      name: item.name,
      styleNumber: item.styleNumber,
      price: item.price,
      color: item.color,
      image: item.image,
    });
    toast.success(`${item.name} added to cart!`);
  };

  const handleShareWishlist = () => {
    navigator.clipboard.writeText(window.location.href + '?wishlist=shared');
    toast.success('Wishlist link copied to clipboard!');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-secondary/60 z-50 animate-fade-in"
        onClick={closeWishlist}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-background z-50 shadow-2xl animate-slide-in-right">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-2">
              <Heart className="w-5 h-5 text-primary fill-primary" />
              <h2 className="font-display font-bold text-xl">Wishlist</h2>
            </div>
            <div className="flex items-center space-x-2">
              {items.length > 0 && (
                <button
                  onClick={handleShareWishlist}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                  title="Share Wishlist"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={closeWishlist}
                className="p-2 hover:bg-muted rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Wishlist Items */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Heart className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">Your wishlist is empty</p>
                <p className="text-muted-foreground mb-6">Save your favorite grips for later!</p>
                <Button onClick={closeWishlist} className="btn-fill-rise btn-fill-dark">
                  Start Browsing
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex space-x-4 p-4 bg-muted/50 rounded-lg group">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-md"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold">{item.name}</h3>
                      <p className="text-xs text-muted-foreground mb-1">Style #{item.styleNumber}</p>
                      <p className="font-bold text-lg">${item.price.toFixed(2)}</p>
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(item)}
                        className="mt-2 btn-fill-rise btn-fill-dark text-xs sm:text-sm rounded-full h-8 sm:h-9 px-3 sm:px-4"
                      >
                        <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                        Add to Cart
                      </Button>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:text-destructive transition-all self-start"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WishlistDrawer;
